/**
 * ADMIN Featured Companies Page
 * จัดการบริษัทแนะนำ - Admin สามารถเพิ่ม/ลบบริษัทจากรายการแนะนำ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiStar,
  FiCheckCircle,
  FiSearch,
  FiRefreshCw,
  FiFilter,
  FiDownload
} from 'react-icons/fi';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { AdminLayout } from '../../components/admin';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useDialog } from '../../hooks/useDialog';
import toast from 'react-hot-toast';
import ExportDialog from '../../components/admin/ExportDialog';
import { API_URL } from '../../services/api';

// Types
interface Company {
  id: string;
  nameTh: string;
  nameEn?: string;
  province: string;
  district?: string;
  isVerified: boolean;
  isFeatured: boolean;
  logoUrl?: string;
  serviceTypes?: string[];
  createdAt: string;
}

interface CompaniesResponse {
  companies: Company[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

const FeaturedCompaniesPage: React.FC = () => {
  const { token } = useAdminAuth();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();

  // State
  const [activeTab, setActiveTab] = useState<'featured' | 'all'>('featured');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState<'all' | 'verified' | 'unverified'>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const perPage = 20;

  // API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    params?: any;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    

    // Build URL with query params
    let url = `${API_URL}${options.endpoint}`;
    if (options.params) {
      const queryString = new URLSearchParams(options.params).toString();
      url += `?${queryString}`;
    }

    const response = await fetch(url, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    const result = response.status === 204 ? {} : await response.json();

    if (!response.ok) {
      throw new Error(result.detail || result.message || `HTTP ${response.status}`);
    }

    return {
      success: true,
      data: result,
      message: result.message
    };
  }, [token]);

  // Export to Excel
  const handleExportExcel = async (dateRange: string) => {
    try {
      setIsExporting(true);

      if (!token) {
        toast.error('ไม่พบ token สำหรับการยืนยันตัวตน');
        return;
      }

      
      const response = await fetch(`${API_URL}/api/admin/companies/export/excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('ไม่สามารถ export ข้อมูลได้');
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `companies_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export Excel สำเร็จ');
      setShowExportDialog(false);
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(error.message || 'เกิดข้อผิดพลาดในการ export');
    } finally {
      setIsExporting(false);
    }
  };

  // Load companies
  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);

      const params: any = {
        page: currentPage,
        per_page: perPage
      };

      // Tab filter
      if (activeTab === 'featured') {
        params.is_featured = 'true';
      }

      // Search filter
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Province filter
      if (provinceFilter) {
        params.province = provinceFilter;
      }

      // Verified filter
      if (verifiedFilter !== 'all') {
        params.is_verified = verifiedFilter === 'verified' ? 'true' : 'false';
      }

      const response = await callApi<CompaniesResponse>({
        method: 'GET',
        endpoint: '/api/admin/companies',
        params
      });

      if (response.success && response.data) {
        setCompanies(response.data.companies || []);
        setTotalPages(response.data.total_pages || 1);
        setTotalCompanies(response.data.total || 0);
      }
    } catch (error: any) {
      console.error('Failed to load companies:', error);
      toast.error(error.message || 'ไม่สามารถโหลดข้อมูลบริษัทได้');
    } finally {
      setLoading(false);
    }
  }, [callApi, activeTab, currentPage, searchTerm, provinceFilter, verifiedFilter]);

  // Load companies when filters change
  useEffect(() => {
    if (token) {
      loadCompanies();
    }
  }, [loadCompanies, token]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, provinceFilter, verifiedFilter, activeTab]);

  // Toggle featured status
  const handleToggleFeatured = async (company: Company) => {
    const action = company.isFeatured ? 'ลบออกจาก' : 'เพิ่มเข้า';

    await showConfirm(
      {
        title: `ยืนยันการ${action}บริษัทแนะนำ`,
        message: `คุณต้องการ${action}บริษัทแนะนำสำหรับ "${company.nameTh}" ใช่หรือไม่?`,
        confirmText: 'ยืนยัน',
        cancelText: 'ยกเลิก',
        variant: company.isFeatured ? 'warning' : 'success'
      },
      async () => {
        setActionLoading(company.id);
        try {
          const response = await callApi({
            method: 'POST',
            endpoint: `/api/admin/companies/${company.id}/feature`
          });

          if (response.success) {
            toast.success(
              company.isFeatured
                ? 'ลบออกจากบริษัทแนะนำเรียบร้อยแล้ว'
                : 'เพิ่มเข้าบริษัทแนะนำเรียบร้อยแล้ว'
            );
            await loadCompanies();
          }
        } catch (error: any) {
          console.error('Failed to toggle featured:', error);
          toast.error(error.message || 'ไม่สามารถเปลี่ยนสถานะได้');
        } finally {
          setActionLoading(null);
        }
      }
    );
  };

  // Get unique provinces for filter
  const provinces = Array.from(new Set(companies.map(c => c.province))).sort();

  // Get badge variant
  const getBadge = (type: 'verified' | 'featured', value: boolean) => {
    if (type === 'verified') {
      return value ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FiCheckCircle className="mr-1" /> ยืนยันแล้ว
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          ยังไม่ยืนยัน
        </span>
      );
    }

    return value ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <FiStar className="mr-1" /> บริษัทแนะนำ
      </span>
    ) : null;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
              <FiStar className="mr-3 h-8 w-8 text-yellow-500" />
              จัดการบริษัทแนะนำ
            </h1>
            <p className="text-gray-600">
              เพิ่ม/ลบบริษัทจากรายการแนะนำ (ทั้งหมด {totalCompanies} รายการ)
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowExportDialog(true)}
            className="flex items-center space-x-2"
          >
            <FiDownload className="w-4 h-4" />
            <span>Export Excel</span>
          </Button>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('featured')}
                className={`${
                  activeTab === 'featured'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                <FiStar className="inline mr-2" />
                บริษัทแนะนำ
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`${
                  activeTab === 'all'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                <FiFilter className="inline mr-2" />
                บริษัททั้งหมด
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาชื่อบริษัท..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Province Filter */}
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={provinceFilter}
              onChange={(e) => setProvinceFilter(e.target.value)}
            >
              <option value="">ทุกจังหวัด</option>
              {provinces.map((province) => (
                <option key={province} value={province}>
                  {province}
                </option>
              ))}
            </select>
          </div>

          {/* Verified Filter */}
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value as 'all' | 'verified' | 'unverified')}
            >
              <option value="all">สถานะทั้งหมด</option>
              <option value="verified">ยืนยันแล้ว</option>
              <option value="unverified">ยังไม่ยืนยัน</option>
            </select>
          </div>
        </div>

        {/* Action Bar */}
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            แสดง {companies.length} จาก {totalCompanies} รายการ
          </p>
          <Button
            variant="secondary"
            leftIcon={<FiRefreshCw />}
            onClick={() => loadCompanies()}
            disabled={loading}
          >
            รีเฟรช
          </Button>
        </div>

        {/* Companies Table */}
        <Card>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600 mr-3" />
                <span className="text-gray-600">กำลังโหลด...</span>
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-12">
                <FiStar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {activeTab === 'featured' ? 'ยังไม่มีบริษัทแนะนำ' : 'ไม่พบบริษัท'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || provinceFilter || verifiedFilter !== 'all'
                    ? 'ลองเปลี่ยนตัวกรอง'
                    : 'เพิ่มบริษัทเข้าสู่รายการแนะนำได้จากแท็บ "บริษัททั้งหมด"'}
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ชื่อบริษัท
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      จังหวัด
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      สถานะยืนยัน
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      บริษัทแนะนำ
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      จัดการ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company, index) => (
                    <motion.tr
                      key={company.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {company.logoUrl && (
                            <img
                              src={company.logoUrl}
                              alt={company.nameTh}
                              className="h-10 w-10 rounded-md object-cover mr-3"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {company.nameTh}
                            </div>
                            {company.nameEn && (
                              <div className="text-sm text-gray-500">
                                {company.nameEn}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{company.province}</div>
                        {company.district && (
                          <div className="text-sm text-gray-500">{company.district}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getBadge('verified', company.isVerified)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getBadge('featured', company.isFeatured)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant={company.isFeatured ? 'destructive' : 'success'}
                          size="sm"
                          onClick={() => handleToggleFeatured(company)}
                          disabled={actionLoading === company.id}
                          loading={actionLoading === company.id}
                          leftIcon={<FiStar />}
                        >
                          {company.isFeatured ? 'ลบออก' : 'เพิ่มเข้า'}
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              หน้า {currentPage} จาก {totalPages}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
              >
                ก่อนหน้า
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
              >
                ถัดไป
              </Button>
            </div>
          </div>
        )}

        {/* Dialog Components */}
        {ConfirmDialog}
        {AlertDialog}

        {/* Export Dialog */}
        <ExportDialog
          isOpen={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          onExport={handleExportExcel}
          isExporting={isExporting}
        />
      </div>
    </AdminLayout>
  );
};

export default FeaturedCompaniesPage;
